import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import { take } from 'lib/array/arrayUtils'
import CategoryTile from 'LuxKit/CategoryTile'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'

function getParentCategoryChildText(intl: IntlShape): Record<number, string> {
  return {
    1: intl.formatMessage({
      id: 'B5iVdpWrzU',
      defaultMessage: 'Fine dining, restaurant & bars, food tours, wine country trips and more.',
      description: 'Sub-heading for Wine & dining experience category tile',
    }),
    9: intl.formatMessage({
      id: 'EAqm5iTJEs',
      defaultMessage: 'Hot air ballooning, watersports, cruises, photoshoots and more.',
      description: 'Sub-heading for Top activities experience category tile',
    }),
    31: intl.formatMessage({
      id: '5FOguRcrER',
      defaultMessage: 'Spa & massage, hot springs, wellness and more.',
      description: 'Sub-heading for Indulge yourself experience category tile',
    }),
    18: intl.formatMessage({
      id: 'Y7604K6378',
      defaultMessage: 'Theme & water parks, museums, galleries, zoos, historical sites and more.',
      description: 'Sub-heading for Attractions & tickets experience category tile',
    }),
    27: intl.formatMessage({
      id: '9CZnxGRfYF',
      defaultMessage: 'Concerts, theatre, live sports and more.',
      description: 'Sub-heading for Live events experience category tile',
    }),
    35: intl.formatMessage({
      id: 'tBLugdacKI',
      defaultMessage: 'Relax at the airport lounge, book a transfer and more.',
      description: 'Sub-heading for Airport lounges experience category tile',
    }),
    40: intl.formatMessage({
      id: 'bEYDrXHWTc',
      defaultMessage: 'The best of the best.',
      description: 'Sub-heading for Lux exclusive experience category tile',
    }),
    48: intl.formatMessage({
      id: 'hQgL9qqEsf',
      defaultMessage: 'Guided tours, private tours, hop-on-hop-off buses, walking tours and more.',
      description: 'Sub-heading for Day tours experience category tile',
    }),
  }
}

interface Props {
  category: App.ExperienceItemCategory
  count: number
  childCategories: Array<App.ExperienceItemCategory>
  to: string
}

function ExperiencesPageCategoryTile(props: Props) {
  const { category, to, childCategories, count } = props
  const intl = useIntl()
  const trackEvent = useTrackExperienceOfferEvent()

  const parentCategoryChildText = useMemo(() => getParentCategoryChildText(intl), [intl])
  const andMoreSuffix = intl.formatMessage({
    id: 'gwwTssPZgQ',
    defaultMessage: 'and more',
    description: 'Suffix appended to child category names on experience category tile',
  })
  const subCategories = useMemo(() =>
    parentCategoryChildText[category.id] ?? (take(childCategories, 4).map(cat => cat.name).join(', ') + ' ' + andMoreSuffix),
  [childCategories, category, parentCategoryChildText, andMoreSuffix])

  const image = useMemo((): App.Image => ({
    id: category.imageId,
    title: category.name,
  }), [category])

  // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  const handleClick = useCallback(() => trackEvent('CATEGORY_CLICKED'), [])

  const callToAction = intl.formatMessage(
    {
      id: 'qTMQbCbwr1',
      defaultMessage: 'View {count, plural, one {# offer} other {# offers}}',
      description: 'CTA text showing number of offers for experience category tile',
    },
    { count },
  )

  return <CategoryTile
    onClick={handleClick}
    to={to}
    image={image}
    heading={category.name}
    subHeading={subCategories}
    align="top"
    callToAction={callToAction}
  />
}

export default ExperiencesPageCategoryTile
